<script setup>
import { ref, onMounted, nextTick } from "vue";
import { modulesForCurrentUser, listModules, currentUser } from "../db/auth.js";
import { useRouter } from "vue-router";
import Swal from "sweetalert2";
import ChatAssistant from "../components/ChatAssistant.vue";
import { listSales } from "../db/sales.js";
import { invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";
import { useSupabase } from "../db/supabaseClient.js";
import gsap from "gsap";

const router = useRouter();
const allModules = ref([]); // todos los módulos
const allowedSet = ref(new Set()); // códigos permitidos
const loading = ref(false);
const debugError = ref("");

// Iconos por código (Material Symbols)
function iconFor(code) {
  const map = {
    dashboard: "space_dashboard",
    inventory: "inventory_2",
    sales: "point_of_sale",
    purchases: "shopping_bag",
    customers: "group",
    suppliers: "store",
    taxes: "request_quote",
    initial_inventory: "playlist_add_check",
    products: "category",
    menu: "restaurant_menu",
    orders: "receipt_long",
    preparation: "soup_kitchen",
    report_sales: "insights",
    daily_sales: "calendar_month",
    daily_profits: "trending_up",
    base_purchases: "storage",
    base_sales: "storage",
    users: "manage_accounts",
    invoicing: "description",
    updates: "system_update",
    backup: "backup",
  };
  return map[code] || "apps";
}

onMounted(async () => {
  loading.value = true;
  const allowed = await modulesForCurrentUser();
  allowedSet.value = new Set(allowed.map((m) => m.code));

  const list = await listModules();
  allModules.value = list.map((m) => ({ code: m.code, name: m.name, icon: iconFor(m.code) }));

  // Asegurar módulos manuales
  const manualModules = [
    { code: "users", name: "Usuarios" },
    { code: "base_purchases", name: "Base Compras" },
    { code: "invoicing", name: "Facturación" },
    { code: "updates", name: "Actualizaciones" },
    { code: "backup", name: "Copia de Seguridad" }
  ];

  manualModules.forEach(m => {
    if (!allModules.value.some((mod) => mod.code === m.code)) {
      allModules.value.push({ code: m.code, name: m.name, icon: iconFor(m.code) });
    }
  });

  // Si es admin, dar acceso explícito a los módulos manuales
  const user = currentUser();
  const role = String(user?.role || "").toLowerCase();
  const isAdmin = role === "admin" || role === "superadmin" || user?.role_id === 1;

  if (isAdmin) {
    manualModules.forEach(m => allowedSet.value.add(m.code));
  }

  const hiddenCodes = new Set(["customers", "orders", "preparation", "base_sales", "updates"]);
  allModules.value = allModules.value.filter((m) => !hiddenCodes.has(m.code));
  hiddenCodes.forEach((code) => allowedSet.value.delete(code));

  loading.value = false;

  // GSAP Animations - wait for DOM to be ready
  await nextTick();
  await new Promise(resolve => setTimeout(resolve, 100)); // Small delay to ensure rendering

  // Set initial state to ensure elements are visible
  gsap.set(".dash-top", { opacity: 1 });
  gsap.set(".card", { opacity: 1 });

  // Animate header
  gsap.from(".dash-top", {
    duration: 0.8,
    y: -30,
    opacity: 0,
    ease: "power3.out"
  });

  // Animate cards with stagger
  const cards = document.querySelectorAll('.card');
  if (cards.length > 0) {
    gsap.from(cards, {
      duration: 0.7,
      y: 40,
      opacity: 0,
      scale: 0.95,
      stagger: 0.06,
      ease: "back.out(1.2)",
      delay: 0.2
    });

    // Add hover animations to unlocked cards
    cards.forEach(card => {
      if (!card.classList.contains('locked')) {
        card.addEventListener('mouseenter', () => {
          gsap.to(card, {
            duration: 0.3,
            y: -8,
            scale: 1.03,
            ease: "power2.out"
          });

          const icon = card.querySelector('.icon');
          if (icon) {
            gsap.to(icon, {
              duration: 0.5,
              rotation: 360,
              scale: 1.1,
              ease: "back.out(1.5)"
            });
          }
        });

        card.addEventListener('mouseleave', () => {
          gsap.to(card, {
            duration: 0.3,
            y: 0,
            scale: 1,
            ease: "power2.inOut"
          });

          const icon = card.querySelector('.icon');
          if (icon) {
            gsap.to(icon, {
              duration: 0.3,
              rotation: 0,
              scale: 1,
              ease: "power2.inOut"
            });
          }
        });
      }
    });
  }

  try {
    // await listen("debug-error", (e) => {
    //   const p = e?.payload;
    //   const msg = typeof p === "string" ? p : (p && (p.message || p.area)) || "";
    //   debugError.value = String(msg || "");
    // });
  } catch { }
  try {
    // if (typeof window !== "undefined") {
    //   window.addEventListener("debug-error", (e) => {
    //     const d = e?.detail || e;
    //     const msg = (d && (d.message || d.area)) || "";
    //     debugError.value = String(msg || "");
    //   });
    // }
  } catch { }
});

async function open(rawCode) {
  const code = String(rawCode || "").trim();
  //console.log("Intentando abrir módulo:", code);

  if (!allowedSet.value.has(code)) {
    Swal.fire({
      icon: "info",
      title: "Acceso bloqueado",
      text: "No tienes permisos para acceder a este apartado.",
      confirmButtonText: "Entendido",
    });
    return;
  }

  if (code === "inventory") router.push("/inventario");
  else if (code === "dashboard") {
    router.push("/dashboard/analytics");
  }
  else if (code === "users") router.push("/usuarios");
  else if (code === "products") router.push("/productos");
  else if (code === "sales") {
    // debugError.value = "Abriendo Ventas…";
    // try { await invoke("emit_debug_error", { area: "sales", message: "Abriendo Ventas…" }); } catch {}
    // try { if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("debug-error", { detail: { area: "sales", message: "Abriendo Ventas…" } })); } catch {}
    try {
      await new Promise((resolve) => setTimeout(resolve, 600));
      const rows = await listSales();
      const usingSB = useSupabase();
      const msg = `Ventas cargadas: ${rows.length} | supabase: ${usingSB ? 'on' : 'off'}`;
      // debugError.value = msg;
      // try { await invoke("emit_debug_error", { area: "sales", message: msg }); } catch {}
      // try { if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("debug-error", { detail: { area: "sales", message: msg } })); } catch {}
      await new Promise((resolve) => setTimeout(resolve, 800));
      await router.push("/ventas");
    } catch (e) {
      const msg = (e && e.message) || String(e);
      // debugError.value = msg;
      // try { await invoke("emit_debug_error", { area: "sales", message: String(msg || "") }); } catch {}
      // try { if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("debug-error", { detail: { area: "sales", message: String(msg || "") } })); } catch {}
    }
  }
  else if (code === "purchases") {
    router.push('/compras');
  }
  else if (code === "base_purchases") {
    router.push('/base-compras');
  }
  else if (code === "suppliers") {
    router.push('/proveedores');
  }
  else if (code === "menu") {
    router.push('/menu');
  }
  else if (code === "taxes") {
    router.push('/impuestos');
  }
  else if (code === "initial_inventory") {
    router.push('/ingredientes');
  }
  else if (code === "invoicing") {
    router.push('/facturacion');
  }
  else if (code === "updates") {
    router.push('/actualizaciones');
  }
  else if (code === "backup") {
    router.push('/backup');
  }
  else if (code === "report_sales") {
    router.push("/reportes/ventas");
  }
  else if (code === "daily_sales") {
    router.push("/reportes/ventas-diarias");
  }
  else if (code === "daily_profits") {
    router.push("/reportes/ganancias-diarias");
  }
  else {
    console.log("Módulo no manejado explícitamente:", code);
  }
}
</script>

<template>
  <main class="dash-wrap">
    <header class="dash-top">
      <h1 class="logo-text" style="font-size: 3rem;">Sushito</h1>
      <p class="subtitle">Dashboard</p>
    </header>

    <section class="grid" v-if="!loading">
      <button v-for="m in allModules" :key="m.code" class="card" :class="{ locked: !allowedSet.has(m.code) }"
        @click="open(m.code)" :title="allowedSet.has(m.code) ? m.name : 'Sin acceso'">
        <span class="material-symbols-rounded icon" aria-hidden="true">{{
          m.icon
        }}</span>
        <span class="label">{{ m.name }}</span>
        <span v-if="!allowedSet.has(m.code)" class="lock">
          <span class="material-symbols-rounded" aria-hidden="true">lock</span>
          <span class="lock-text">Bloqueado</span>
        </span>
      </button>
    </section>
    <p v-else class="muted">Cargando módulos…</p>
    <!-- <p v-if="debugError" class="debug-box">{{ debugError }}</p> -->
  </main>
  <ChatAssistant />
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Cinzel:wght@600;700;800&display=swap');

.dash-wrap {
  display: grid;
  gap: 1.5rem;
  padding: 2rem;
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  min-height: 100vh;
  position: relative;
  overflow: hidden;
}

.dash-wrap::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 10% 20%, rgba(198, 146, 61, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 90% 80%, rgba(223, 192, 94, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 50% 50%, rgba(247, 237, 125, 0.05) 0%, transparent 70%);
  pointer-events: none;
  z-index: 0;
}

.dash-top {
  display: grid;
  gap: 0.25rem;
  position: relative;
  z-index: 1;
  padding: 1rem;
  background: rgba(20, 24, 30, 0.95);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-radius: 24px;
  border: 1px solid rgba(198, 146, 61, 0.3);
  box-shadow: 0 8px 32px rgba(198, 146, 61, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.title {
  font-size: 2.5rem;
  font-weight: 900;
  font-family: 'Playfair Display', 'Cinzel', 'Times New Roman', serif;
  text-transform: uppercase;
  background: linear-gradient(135deg,
      #FFD700 0%,
      #FFE55C 10%,
      #FFD700 20%,
      #F4D03F 35%,
      #FFD700 45%,
      #C6923D 60%,
      #9D6B27 75%,
      #C6923D 85%,
      #FFD700 95%,
      #FFE55C 100%);
  background-size: 300% 300%;
  animation: shimmer 2.5s ease-in-out infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 0.1em;
  text-shadow:
    3px 4px 0px rgba(0, 0, 0, 1),
    5px 7px 12px rgba(0, 0, 0, 0.9),
    7px 10px 20px rgba(0, 0, 0, 0.8),
    0px 2px 4px rgba(255, 215, 0, 0.6),
    0px -2px 2px rgba(114, 68, 16, 0.8),
    inset 0px 1px 0px rgba(255, 255, 255, 0.3);
  filter:
    drop-shadow(3px 5px 10px rgba(0, 0, 0, 0.9)) drop-shadow(1px 2px 4px rgba(255, 215, 0, 0.4));
  position: relative;
  transform: perspective(100px) rotateX(2deg);
}

@keyframes shimmer {

  0%,
  100% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }
}

.subtitle {
  color: rgba(255, 255, 255, 0.7);
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1.5rem;
  position: relative;
  z-index: 1;
}

.card {
  display: grid;
  place-items: center;
  gap: 0.75rem;
  border-radius: 24px;
  padding: 2rem 1.5rem;
  border: 1px solid rgba(198, 146, 61, 0.3);
  background: rgba(20, 24, 30, 0.95);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow: 0 8px 32px rgba(198, 146, 61, 0.2),
    0 2px 8px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;
  transform-style: preserve-3d;
  perspective: 1000px;
}

.card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #C6923D, #DFC05E, #F7ED7D, #DFC05E);
  opacity: 0;
  transition: opacity 0.3s;
}

.card::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(198, 146, 61, 0.3), transparent 70%);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.card:hover::before {
  opacity: 1;
}

.card:hover::after {
  width: 300px;
  height: 300px;
}

.card:hover {
  border-color: rgba(198, 146, 61, 0.6);
  box-shadow: 0 20px 60px rgba(198, 146, 61, 0.4),
    0 8px 20px rgba(223, 192, 94, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.card.locked {
  opacity: 0.6;
  cursor: not-allowed;
  border-style: dashed;
  background: linear-gradient(135deg, rgba(200, 200, 200, 0.3) 0%, rgba(150, 150, 150, 0.2) 100%);
}

.card.locked:hover {
  transform: none;
  box-shadow: 0 8px 32px rgba(198, 146, 61, 0.2),
    0 2px 8px rgba(0, 0, 0, 0.3);
}

.icon {
  font-size: 36px;
  color: #000000;
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  border-radius: 18px;
  padding: 1rem;
  box-shadow: 0 8px 24px rgba(198, 146, 61, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  z-index: 1;
}

.lock {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.25rem;
  color: rgba(255, 255, 255, 0.6);
}

.lock-text {
  font-size: 0.85rem;
}

.label {
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  position: relative;
  z-index: 1;
  letter-spacing: -0.01em;
}

/* En modo claro, el texto debe ser oscuro para contrastar con el fondo claro */
@media (prefers-color-scheme: light) {
  .dash-wrap {
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  }

  .card {
    background: rgba(255, 255, 255, 0.98);
    border-color: rgba(198, 146, 61, 0.4);
  }

  .label {
    color: #1a1610;
  }

  .muted {
    color: rgba(26, 22, 16, 0.7);
  }

  .lock {
    color: rgba(26, 22, 16, 0.6);
  }

  .dash-top {
    background: rgba(255, 255, 255, 0.98);
    border-color: rgba(198, 146, 61, 0.4);
  }

  .subtitle {
    color: rgba(26, 22, 16, 0.7);
  }
}

:root[data-theme="light"] .dash-wrap {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

:root[data-theme="light"] .card {
  background: rgba(255, 255, 255, 0.98);
  border-color: rgba(198, 146, 61, 0.4);
}

:root[data-theme="light"] .label {
  color: #1a1610;
}

:root[data-theme="light"] .muted {
  color: rgba(26, 22, 16, 0.7);
}

:root[data-theme="light"] .lock {
  color: rgba(26, 22, 16, 0.6);
}

:root[data-theme="light"] .dash-top {
  background: rgba(255, 255, 255, 0.98);
  border-color: rgba(198, 146, 61, 0.4);
}

:root[data-theme="light"] .subtitle {
  color: rgba(26, 22, 16, 0.7);
}

.muted {
  color: rgba(255, 255, 255, 0.6);
}

.debug-box {
  position: fixed;
  top: 76px;
  left: 16px;
  z-index: 3000;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border: 1px solid #ef4444;
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
  font-weight: 600;
}

@media (prefers-color-scheme: dark) {
  .card {
    background: var(--surface);
    border-color: var(--border);
  }
}
</style>
